// Copyright ©️ 2026 eVolve MEP, LLC

import React from 'react';

import { Button, Text, Group, Stack, ActionIcon, Collapse, Loader } from '@mantine/core';

import { EvolveIcon } from 'assets/icons/EvolveIcon';
import { AgGridStyleTooltip } from 'components/Mantine/AgGridStyleTooltip';
import { isNotNil } from 'helpers/isNotNil';

import { useBendCreatorCartContext, type CartBend } from '../Context/useBendCreatorCartContext';
import { BendDescription } from '../Description/BendDescription';
import { BendDimensions } from '../Description/BendDimensions';
import { BendMaterial } from '../Description/BendMaterial';
import { BendQuantityStepper } from '../Fields/BendQuantityStepper';
import { type BendCreatorStep, bendCreatorSteps } from './BendCreatorStepPanel';

type Props = {
  step: BendCreatorStep | null;
  onChangeStep: (step: BendCreatorStep) => void;
  index: number;
  bend: CartBend;
};

const stepDescriptionComponents = {
  'Material': BendMaterial,
  'Bend Type': ({ bend }: { bend: CartBend }) => bend.bendType,
  'Dimensions': BendDimensions,
  'Review': ({ bend }: { bend: CartBend }) => bend.partName,
} as const satisfies Record<BendCreatorStep, React.FC<{ bend: CartBend }>>;

export const BendListBend = ({ index, bend, step, onChangeStep }: Props) => {
  const { restoringBend, selectedItem, onSelect, update, append, remove } = useBendCreatorCartContext();
  const isSelected = selectedItem?.draftId === bend.draftId;
  return (
    <Stack p="sm" style={{ borderBottom: '1px solid var(--mantine-color-gray-1)' }}>
      <Group justify="space-between">
        <Group>
          <Text c="dimmed">#{index}</Text>
          <Text fw={600}>{bend.partName}</Text>
        </Group>
        <Group>
          <AgGridStyleTooltip label="Edit Bend">
            <ActionIcon
              variant={isSelected ? 'outline' : 'light'}
              onClick={() => onSelect(bend.draftId)}
              disabled={restoringBend}
            >
              {restoringBend ? <Loader size="xs" /> : <EvolveIcon icon="Edit" />}
            </ActionIcon>
          </AgGridStyleTooltip>
          <AgGridStyleTooltip label="Delete">
            <ActionIcon variant="light" onClick={() => remove(bend.draftId)}>
              <EvolveIcon icon="Delete" />
            </ActionIcon>
          </AgGridStyleTooltip>
        </Group>
      </Group>
      <BendDescription bend={bend} />
      <Collapse expanded={isSelected}>
        <Button.Group orientation="vertical">
          {bendCreatorSteps.map((bendStep) => {
            const Component = stepDescriptionComponents[bendStep];
            return (
              <Button
                key={bendStep}
                variant={step === bendStep ? 'outline' : 'light'}
                onClick={() => onChangeStep(bendStep)}
                styles={{
                  label: {
                    justifyContent: 'space-between',
                    flex: 1,
                    textAlign: 'left',
                  },
                }}
              >
                <Text fw={600} flex={1}>
                  {bendStep}
                </Text>
                <Text c="dimmed" ff="monospace" maw={100} truncate>
                  <Component bend={bend} />
                </Text>
              </Button>
            );
          })}
        </Button.Group>
      </Collapse>
      <Group wrap="nowrap">
        <Button miw={140} variant="light" leftSection={<EvolveIcon icon="Copy" />} onClick={() => append({ ...bend })}>
          Duplicate
        </Button>
        <AgGridStyleTooltip label="Quantity">
          <BendQuantityStepper
            onChange={(value) => {
              if (isNotNil(value)) {
                update(bend.draftId, { ...bend, quantity: value });
              }
            }}
            value={bend.quantity}
          />
        </AgGridStyleTooltip>
      </Group>
    </Stack>
  );
};
